<template>
  <ion-page>
    <PageHeaderWithMenu title="Тестирование" :scrolled="headerScrolled" />
    <ion-content
      :class="[isMobile ? 'main-background' : 'liga-web-background', 'testing-content']"
      :fullscreen="true"
      @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="loadMyTests($event)">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <!-- Список тестов -->
      <div v-show="!currentTest" class="testing-list-wrap">
        <p v-if="loading" class="testing-hint">Загрузка…</p>
        <p v-else-if="!myTests.length" class="testing-hint">Вам пока не назначены тесты.</p>
        <div v-else class="testing-list">
          <div
            v-for="t in myTests"
            :key="t.id"
            :class="['testing-card', { 'testing-card--passed': t.passed }]"
            @click="!t.passed && openTest(t)">
            <div class="testing-card-icon">
              <ion-icon :icon="documentTextOutline"></ion-icon>
            </div>
            <div class="testing-card-body">
              <h3 class="testing-card-title">{{ t.name }}</h3>
              <p v-if="t.description" class="testing-card-desc">{{ t.description }}</p>
              <div class="testing-card-meta">
                <span v-if="t.passed" class="testing-status testing-status--passed">
                  <ion-icon :icon="checkmarkCircle"></ion-icon> Пройден
                </span>
                <span v-else-if="t.attemptsLeft" class="testing-status testing-status--open">Доступен</span>
                <span v-else class="testing-status testing-status--used">Попытки исчерпаны</span>
                <span v-if="t.lastScore != null" class="testing-score">Результат: {{ t.lastScore }}%</span>
              </div>
            </div>
            <ion-icon :icon="chevronForwardOutline" class="testing-card-arrow"></ion-icon>
          </div>
        </div>
      </div>

      <!-- Прохождение теста (v-if, чтобы не обращаться к currentTest при null) -->
      <div v-if="currentTest" class="testing-take-wrap">
        <div class="testing-take-back-bar">
          <ion-button fill="clear" class="testing-back-btn" @click="closeTest">
            <ion-icon :icon="arrowBackOutline"></ion-icon>
            <span>К списку тестов</span>
          </ion-button>
        </div>
        <div class="testing-take-header">
          <h2 class="testing-take-title">{{ currentTest.name }}</h2>
          <p v-if="currentTest.description" class="testing-take-desc">{{ currentTest.description }}</p>
          <div v-if="attemptStartedAt" class="testing-timer-wrap" :data-tick="timerTick">
            <span class="testing-timer-label">Время: {{ timerElapsed }}</span>
            <span v-if="currentTest.timeLimitMinutes != null" class="testing-timer-remaining">
              {{ timeExpired ? 'Время вышло' : `Осталось: ${timerRemaining}` }}
            </span>
          </div>
        </div>
        <div v-if="testLoading" class="testing-take-loading">
          <ion-spinner name="bubbles"></ion-spinner>
        </div>
        <div v-else class="testing-take-questions">
          <div
            v-for="q in (currentTest.questions || [])"
            :key="q.id"
            class="testing-question-block">
            <p class="testing-question-text">{{ q.text }}</p>
            <div v-if="q.multipleChoice" class="testing-answers">
              <div
                v-for="a in q.answers"
                :key="a.id"
                class="testing-answer-row"
                role="button"
                tabindex="0"
                @click="toggleAnswer(q.id, a.id)"
                @keydown.enter.space.prevent="toggleAnswer(q.id, a.id)">
                <ion-checkbox
                  :checked="isSelected(q.id, a.id)"
                  tabindex="-1"></ion-checkbox>
                <span class="testing-answer-label ion-text-wrap">{{ a.text }}</span>
              </div>
            </div>
            <ion-radio-group v-else :value="(selectedAnswers[q.id] || [])[0]" @ionChange="onSingleChange(q.id, $event)" class="testing-answers">
              <ion-item
                v-for="a in (q.answers || [])"
                :key="a.id"
                class="testing-answer-item"
                button
                :detail="false"
                @click="selectSingleAnswer(q.id, a.id)">
                <ion-radio :value="a.id" label-placement="end" :aria-label="a.text">
                  <span class="ion-text-wrap">{{ a.text }}</span>
                </ion-radio>
              </ion-item>
            </ion-radio-group>
          </div>
        </div>
        <div v-if="currentTest && !testLoading" class="testing-take-actions">
          <ion-button expand="block" class="admin-primary-btn" @click="submitTest" :disabled="submitting || timeExpired">
            {{ submitting ? 'Отправка…' : 'Завершить тест' }}
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonItem,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  toastController,
} from '@ionic/vue';
import {
  arrowBackOutline,
  checkmarkCircle,
  chevronForwardOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import type { IMyTestVM, ITestPublicVM } from '@/services/admin.service';
import AdminService from '@/services/admin.service';

export default defineComponent({
  name: 'Testing',
  components: {
    IonButton,
    IonCheckbox,
    IonContent,
    IonIcon,
    IonItem,
    IonPage,
    IonRadio,
    IonRadioGroup,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      arrowBackOutline,
      checkmarkCircle,
      chevronForwardOutline,
      documentTextOutline,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      headerScrolled: false,
      loading: false,
      myTests: [] as IMyTestVM[],
      currentTest: null as ITestPublicVM | null,
      testLoading: false,
      selectedAnswers: {} as Record<number, number[]>,
      submitting: false,
      attemptStartedAt: null as string | null,
      timerIntervalId: null as ReturnType<typeof setInterval> | null,
      timerTick: 0,
    };
  },
  mounted() {
    (this as { _testingMounted?: boolean })._testingMounted = true;
  },
  beforeUnmount() {
    (this as { _testingMounted?: boolean })._testingMounted = false;
    this.stopTimerInterval();
  },
  async ionViewWillEnter() {
    await this.loadMyTests();
  },
  computed: {
    timerElapsed(): string {
      void this.timerTick; // зависимость для пересчёта раз в секунду
      const started = this.attemptStartedAt;
      if (!started) return '00:00';
      const sec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      const m = Math.floor(sec / 60);
      const h = Math.floor(m / 60);
      return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    },
    timerRemaining(): string {
      void this.timerTick;
      const limit = this.currentTest?.timeLimitMinutes;
      const started = this.attemptStartedAt;
      if (limit == null || !started) return '';
      const elapsedSec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      const leftSec = Math.max(0, limit * 60 - elapsedSec);
      const m = Math.floor(leftSec / 60);
      const h = Math.floor(m / 60);
      return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    },
    timeExpired(): boolean {
      void this.timerTick;
      const limit = this.currentTest?.timeLimitMinutes;
      const started = this.attemptStartedAt;
      if (limit == null || !started) return false;
      const elapsedSec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      return elapsedSec >= limit * 60;
    },
  },
  methods: {
    startTimerInterval() {
      if (this.timerIntervalId) {
        clearInterval(this.timerIntervalId);
        this.timerIntervalId = null;
      }
      this.timerIntervalId = setInterval(() => {
        this.timerTick = Date.now();
        this.$forceUpdate();
      }, 1000);
    },
    stopTimerInterval() {
      if (this.timerIntervalId) {
        clearInterval(this.timerIntervalId);
        this.timerIntervalId = null;
      }
    },
    onContentScroll(e: Event) {
      const ev = e as CustomEvent;
      this.headerScrolled = (ev.detail?.scrollTop ?? 0) > 30;
    },
    async loadMyTests(event?: CustomEvent) {
      this.loading = true;
      try {
        this.myTests = await this.adminService.getMyTests();
      } catch (e) {
        console.error('loadMyTests', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки списка тестов',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.loading = false;
        (event as CustomEvent & { target?: { complete?: () => void } })?.target?.complete?.();
      }
    },
    async openTest(t: IMyTestVM) {
      if (!t.attemptsLeft && !t.hasDraft) {
        const toast = await toastController.create({
          message: 'Попытки исчерпаны',
          duration: 2000,
          position: 'top',
          color: 'warning',
        });
        await toast.present();
        return;
      }
      this.testLoading = true;
      this.currentTest = null;
      this.selectedAnswers = {};
      try {
        const test = await this.adminService.getTest(t.id);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        this.currentTest = test;
        const draft = await this.adminService.getTestDraft(t.id);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        if (draft?.startedAt) {
          this.attemptStartedAt = draft.startedAt;
          this.$nextTick(() => this.startTimerInterval());
        }
        if (draft?.selectedAnswersByQuestion) {
          const normalized: Record<number, number[]> = {};
          for (const [qId, ans] of Object.entries(draft.selectedAnswersByQuestion)) {
            normalized[Number(qId)] = Array.isArray(ans) ? ans.map(Number) : [];
          }
          this.selectedAnswers = normalized;
        }
      } catch (e) {
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        console.error('openTest', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки теста',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        if ((this as { _testingMounted?: boolean })._testingMounted) {
          this.testLoading = false;
        }
      }
    },
    closeTest() {
      this.currentTest = null;
      this.selectedAnswers = {};
      this.attemptStartedAt = null;
      this.stopTimerInterval();
      this.$nextTick(() => {
        this.loadMyTests();
      });
    },
    isSelected(questionId: number, answerId: number): boolean {
      const arr = this.selectedAnswers[questionId];
      return Array.isArray(arr) && arr.includes(answerId);
    },
    toggleAnswer(questionId: number, answerId: number) {
      const arr = this.selectedAnswers[questionId] || [];
      const idx = arr.indexOf(answerId);
      if (idx >= 0) {
        this.selectedAnswers = {
          ...this.selectedAnswers,
          [questionId]: arr.filter((id) => id !== answerId),
        };
      } else {
        this.selectedAnswers = {
          ...this.selectedAnswers,
          [questionId]: [...arr, answerId],
        };
      }
    },
    onSingleChange(questionId: number, event: CustomEvent) {
      const value = event.detail?.value;
      if (value != null) this.selectSingleAnswer(questionId, Number(value));
    },
    selectSingleAnswer(questionId: number, answerId: number) {
      this.selectedAnswers = {
        ...this.selectedAnswers,
        [questionId]: [answerId],
      };
    },
    async submitTest() {
      if (!this.currentTest) return;
      this.submitting = true;
      const SUBMIT_TIMEOUT_MS = 25000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), SUBMIT_TIMEOUT_MS);
      });
      try {
        const result = await Promise.race([
          this.adminService.submitTest(this.currentTest.id, this.selectedAnswers),
          timeoutPromise,
        ]);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        const toast = await toastController.create({
          message: result.passed
            ? `Тест пройден! Результат: ${result.score}%`
            : `Результат: ${result.score}%. Порог: ${this.currentTest.passingThreshold}%`,
          duration: 4000,
          position: 'top',
          color: result.passed ? 'success' : 'warning',
        });
        await toast.present();
        this.closeTest();
      } catch (e) {
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        console.error('submitTest', e);
        const isTimeout = e instanceof Error && e.message === 'TIMEOUT';
        const toast = await toastController.create({
          message: isTimeout ? 'Превышено время ожидания. Проверьте интернет и попробуйте снова.' : 'Ошибка отправки',
          duration: 4000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        if ((this as { _testingMounted?: boolean })._testingMounted) {
          this.submitting = false;
        }
      }
    },
  },
});
</script>

<style scoped>
.testing-content {
  --background: var(--ion-background-color);
}

.testing-list-wrap,
.testing-take-wrap {
  padding: 16px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
}

.testing-hint {
  color: var(--ion-text-color);
  opacity: 0.8;
  text-align: center;
  padding: 24px;
}

.testing-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.testing-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: transparent;
  border-radius: 12px;
  border: 1px solid var(--ion-border-color, rgba(255, 255, 255, 0.06));
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}

.testing-card:hover {
  transform: translateY(-2px);
  border-color: rgba(232, 93, 4, 0.3);
}

.testing-card--passed {
  cursor: default;
  opacity: 0.9;
}

.testing-card--passed:hover {
  transform: none;
  border-color: var(--ion-border-color, rgba(255, 255, 255, 0.06));
}

.testing-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: transparent;
  color: var(--ion-color-orange);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.testing-card-icon ion-icon {
  font-size: 28px;
}

.testing-card-body {
  flex: 1;
  min-width: 0;
}

.testing-card-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.testing-card-desc {
  margin: 0 0 8px;
  font-size: 0.85rem;
  color: var(--ion-text-color);
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.testing-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
}

.testing-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.testing-status ion-icon {
  font-size: 0.9rem;
}

.testing-status--passed {
  background: var(--ion-color-success);
  color: white;
}

.testing-status--draft {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.testing-status--open {
  background: var(--ion-color-orange);
  color: white;
}

.testing-status--used {
  background: var(--ion-color-medium);
  color: var(--ion-color-medium-contrast);
}

.testing-score {
  color: var(--ion-text-color);
  opacity: 0.8;
}

.testing-card-arrow {
  color: var(--ion-color-medium);
  font-size: 20px;
}

.testing-take-back-bar {
  margin: -16px -16px 16px -16px;
  padding: 8px 8px 8px 0;
  background: var(--ion-color-step-150);
  border-bottom: 1px solid var(--ion-border-color);
}

.testing-take-back-bar .testing-back-btn {
  margin: 0;
  --color: var(--ion-color-orange);
  --color-hover: var(--ion-color-orange-tint);
  --color-activated: var(--ion-color-orange-tint);
  color: var(--ion-color-orange) !important;
  font-weight: 500;
  --padding-start: 8px;
}

.testing-take-back-bar .testing-back-btn ion-icon,
.testing-take-back-bar .testing-back-btn span {
  color: var(--ion-color-orange) !important;
}

.testing-take-back-bar .testing-back-btn ion-icon {
  margin-right: 6px;
  font-size: 1.25rem;
}

.testing-take-header {
  margin-bottom: 20px;
}

.testing-take-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.testing-take-desc {
  margin: 0;
  font-size: 0.9rem;
  color: var(--ion-text-color);
  opacity: 0.85;
}

.testing-timer-wrap {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  color: var(--ion-text-color);
  opacity: 0.9;
}

.testing-timer-label {
  font-weight: 600;
}

.testing-timer-remaining {
  color: var(--ion-color-orange);
}

.testing-take-loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.testing-question-block {
  margin-bottom: 24px;
  padding: 16px;
  background: transparent;
  border-radius: 12px;
  border: 1px solid var(--ion-border-color, rgba(255, 255, 255, 0.06));
}

.testing-question-text {
  margin: 0 0 12px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.testing-answers {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.testing-answer-item {
  --background: transparent;
  --background-hover: transparent;
  --background-activated: transparent;
  --background-focused: transparent;
  --padding-start: 0;
  --inner-padding-end: 0;
  color: var(--ion-text-color);
}

/* Вариант с чекбоксами: свой контейнер — чекбокс слева, текст сразу справа */
.testing-answer-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  cursor: pointer;
  color: var(--ion-text-color);
}
.testing-answer-row .testing-answer-label {
  flex: 1;
  min-width: 0;
  text-align: start;
}

.testing-take-actions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--ion-border-color);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.testing-take-actions ion-button {
  --border-radius: 12px;
  font-weight: 600;
  min-height: 48px;
}

/* Кнопки в стиле приложения: чёрно-оранжевые */
.testing-take-actions ion-button[fill="outline"] {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-hover: var(--ion-color-orange-tint);
  --color-activated: var(--ion-color-orange-tint);
}

/* Кнопка «Завершить тест» — как «Создать тест» в админке (admin-primary-btn) */
.testing-take-actions ion-button.admin-primary-btn,
.testing-take-actions ion-button.admin-primary-btn.ion-activated,
.testing-take-actions ion-button.admin-primary-btn.ion-focused {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange-tint);
  --color: #fff;
}

/* Чекбоксы: светлый фон при отмеченном, оранжевая рамка и галочка — не сливаются с фоном */
.testing-answers ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: rgba(255, 255, 255, 0.95);
  --checkmark-color: var(--ion-color-orange);
  --border-color: var(--ion-color-medium);
  --border-color-checked: var(--ion-color-orange);
  --border-width: 2px;
}

/* Радио: видимый пустой круг (тёмная обводка), оранжевый при выборе, размер как у чекбоксов */
.testing-answers ion-radio {
  --color: var(--ion-color-step-600, #666);
  --color-checked: var(--ion-color-orange);
  --inner-width: 10px;
  --inner-height: 10px;
  --outer-width: 22px;
  --outer-height: 22px;
  --border-width: 2px;
  --border-color: var(--ion-color-step-600, #666);
  --border-color-checked: var(--ion-color-orange);
}
.testing-answers ion-radio::part(container) {
  width: 22px;
  height: 22px;
  border: 2px solid var(--ion-color-step-600, #666);
  border-radius: 50%;
  background: transparent;
}
.testing-answers ion-radio.ion-checked::part(container) {
  border-color: var(--ion-color-orange);
  background: transparent;
}
</style>

<!-- Стили без scoped: переопределение кнопок Ionic (переменные пробрасываются в shadow DOM) -->
<style>
ion-content.testing-content .testing-take-back-bar ion-button.testing-back-btn {
  --color: var(--ion-color-orange);
  --color-hover: var(--ion-color-orange-tint);
  --color-activated: var(--ion-color-orange-tint);
}

/* Кнопка «Завершить тест» — те же стили, что у admin-primary-btn в админке тестов */
ion-content.testing-content .testing-take-actions ion-button.admin-primary-btn {
  --background: var(--ion-color-orange);
  --background-hover: var(--ion-color-orange-tint);
  --background-activated: var(--ion-color-orange-tint);
  --color: #fff;
}
</style>
